import { ItemInfo, ItemPatch } from '../../../shared/types/itemInfo';
import { FeatureCollection, MultiPolygon, PolyCoords } from '../../../shared/types/MapShapes';
import { PrivilegeValue } from '../../../shared/privileges/privileges';

/**
 * Puerto del item (lote) y su polígono: endpoints `/ranch/...`, iguales en todos los backends actuales.
 * Lo usa la página de item (`RouteKey.Item`) por `@Inject(InjectionKey.ItemApi)`.
 */

export interface ItemSaveResponse {
    /** URL a la que vuelve el usuario después de guardar (backends con acceso por token). */
    redirect_url?: string | null;
    [key: string]: any;
}

/**
 * Filtros del "Listado de items precargados". Sus opciones llegan juntas en `GET /ranch/list/options/`; el adaptador
 * mapea cada clave de backend (`years`, `departments`, `crop_types`) a este enum.
 */
export enum PreloadedFilter {
    Campaign = 'campaign',
    Department = 'department',
    CropType = 'cropType'
}

/**
 * Una opción cruda de `GET /ranch/list/options/`: `{ id, description }`. Cada lista viene en el orden de backend (en
 * campañas, la primera es la más reciente). El adaptador la normaliza a `FilterOption`.
 */
export interface PreloadedFilterOptionResponse {
    id: number | string;
    description: string;
}

/**
 * Respuesta cruda de `GET /ranch/list/options/`: las opciones de los tres filtros en un solo objeto. Las claves son
 * opcionales (el formato puede evolucionar): una clave ausente o que no sea lista = sin opciones.
 */
export interface PreloadedFilterOptionsResponse {
    years?: PreloadedFilterOptionResponse[];
    departments?: PreloadedFilterOptionResponse[];
    crop_types?: PreloadedFilterOptionResponse[];
}

/**
 * Opción de un filtro de lotes pre-cargados tal como la usa la UI. `id` es texto (un entero de backend) y se manda en
 * el CSV del parámetro del filtro (`years`, `departments`, `crop_types`).
 */
export interface FilterOption {
    id: string;
    label: string;
}

/** Opciones normalizadas de cada filtro, en el orden de backend (sin opciones = `[]`). */
export type PreloadedFilterOptions = Record<PreloadedFilter, FilterOption[]>;

/** Selección de los tres filtros (ids de `FilterOption`). Lista vacía = todos: el parámetro se omite. */
export interface PreloadedItemsFilters {
    campaigns: string[];
    departments: string[];
    cropTypes: string[];
}

/** Un lote de `GET /ranch/list/` (`list`). `campaign` es el texto de la campaña (p. ej. `"24/25"`); puede faltar. */
export interface PreloadedItemResponse {
    id: number;
    name: string;
    campaign?: string;
}

export interface PreloadedItemsResponse {
    list: PreloadedItemResponse[];
    /** Polígonos de los lotes (`properties`: `name`, `id`, `campaign`). */
    polygons: MultiPolygon;
    /**
     * Límites de los departamentos del filtro, unidos en una FeatureCollection para dibujarlos en el mapa igual que el
     * `department_polygon` del item. El adaptador la arma desde `PreloadedDepartmentLimitResponse[]`; `null` si ningún
     * departamento trae polígono.
     */
    departments: FeatureCollection | null;
}

/**
 * Cada elemento de `departments` en la respuesta cruda de `GET /ranch/list/` (un departamento del combobox con su
 * límite). `polygon` puede ser `null` (departamento sin límite cargado): no se dibuja.
 */
export interface PreloadedDepartmentLimitResponse {
    id: number;
    name: string;
    polygon: FeatureCollection | null;
}

/** Respuesta de `/ranch/area/` (tipada antes como `FeatureCollection`). */
export interface PolygonAreaResponse {
    calculated_area: number;
    area_diff_percent: number;
    [key: string]: any;
}

/**
 * Respuesta cruda de `GET /ranch/` (acceso por token). Ejemplo real (backend beta, ver tests/unit/fixtures/ranch-item.ts):
 * `tests/unit/fixtures/ranch-item.ts`. Si el backend cambia, se actualiza este DTO y el adaptador normaliza a
 * `ItemInfo`; la página no ve el JSON crudo.
 */
export interface RanchItemResponse {
    /** Id numérico del ranch (no es el `:id` de la URL, que puede ser un identificador de texto). */
    id: number;
    name: string;
    crop_type: string;
    declared_area: number;
    /** `FeatureCollection` con una feature `MultiPolygon`, o `null` si el item no tiene polígono. */
    polygon: FeatureCollection | null;
    using_original_polygon?: boolean;
    department: string;
    department_polygon: FeatureCollection | null;
    province: string;
    campaign: string;
    /** `PRIVILEGE_KEYS`: 0 edición, 1 solo lectura, 2 denuncia. */
    privilege: PrivilegeValue;
    /** A dónde vuelve el usuario al guardar/salir; puede traer `centroid_lat` / `centroid_long` (respaldo del centroide). */
    redirect_url: string;
    area?: number;
    /** Centroide del polígono. Fuente principal; si falta o no es válido se usa el de la `redirect_url`. */
    centroid?: RanchCentroidResponse | null;
}

/** `centroid` de `GET /ranch/`: cualquiera de los dos puede venir `null`. */
export interface RanchCentroidResponse {
    latitude: number | null;
    longitude: number | null;
}

export interface ItemPort {
    /**
     * Detalle del item (`privilege`, `redirect_url`, polígono, límites del departamento, indicadores).
     * `itemId` es el `:id` de la URL tal cual (texto): según el backend puede no ser numérico (p. ej. un identificador
     * externo) y el backend resuelve el item a partir del token; el adaptador decide qué mandar.
     */
    getItemDetail(itemId: string): Promise<ItemInfo>;
    /** Guarda el polígono del item. */
    addPolygonToItem(config: ItemPatch): Promise<ItemSaveResponse>;
    /**
     * Opciones de los tres filtros de lotes pre-cargados en un solo request, cada lista en el orden de backend (en
     * campañas, la primera es la más reciente). `customerId`: aseguradora activa; se manda como `customer` solo si es
     * un número (`0` incluido). `ranchId`: `id` de la respuesta de `getItemDetail` (no el `:id` de la URL); se manda
     * como `ranch_id` solo si es un número.
     */
    getPreloadedFilterOptions(customerId?: number | null, ranchId?: number | null): Promise<PreloadedFilterOptions>;
    /**
     * Lotes pre-cargados que cumplen los filtros (combinados). Un filtro vacío = todos (su parámetro se omite).
     * `customerId` y `ranchId`: igual que en `getPreloadedFilterOptions`.
     */
    getPreloadedItems(
        filters: PreloadedItemsFilters,
        customerId?: number | null,
        ranchId?: number | null
    ): Promise<PreloadedItemsResponse>;
    /** Convierte un KML/KMZ (`FormData` con `file`) a GeoJSON. */
    getGeojsonFile(formData: FormData): Promise<FeatureCollection>;
    /** Área del polígono y diferencia contra el área declarada. */
    getPolygonArea(config: { polygon: PolyCoords }): Promise<PolygonAreaResponse>;
}
